import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, switchMap } from 'rxjs';
import { ConfigService } from '@app/core/config/config.service';
import { SKIP_API_KEY } from '@app/data/data-sources/remote/postal-code-remote.data-source';

/**
 * Detecta si la URL es una petición a S3 (presigned URL).
 * S3 URLs típicas: *.s3.amazonaws.com, *.s3.*.amazonaws.com, s3.*.amazonaws.com
 */
function isS3Request(url: string): boolean {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    return (
      host.includes('s3.amazonaws.com') ||
      host.includes('.s3.') ||
      host.startsWith('s3.')
    );
  } catch {
    return false;
  }
}

/**
 * Añade el header x-api-key a todas las peticiones HTTP excepto:
 * - Peticiones a S3 (presigned URLs).
 * - Peticiones marcadas con `SKIP_API_KEY=true` en el HttpContext (servicios
 *   de terceros como SEPOMEX, donde no debemos enviar la clave de nuestro
 *   backend).
 *
 * La clave se obtiene de config.json (xApiKey). Espera a que el config esté cargado.
 */
export const apiKeyInterceptor: HttpInterceptorFn = (req, next) => {
  if (isS3Request(req.url) || req.context.get(SKIP_API_KEY)) {
    return next(req);
  }

  const configService = inject(ConfigService);

  const addHeaderIfReady = () => {
    const apiKey = configService.xApiKey;
    if (!apiKey) {
      return next(req);
    }
    const cloned = req.clone({
      setHeaders: { 'x-api-key': apiKey },
    });
    return next(cloned);
  };

  if (configService.isLoaded) {
    return addHeaderIfReady();
  }

  return from(configService.load()).pipe(switchMap(() => addHeaderIfReady()));
};
